/**
 * Layer Key FAB
 *
 * A small floating "info" button in the map's upper-right (below the context bar)
 * that opens a compact panel describing the layers currently visible on the map.
 * Each row shows:
 *   - the layer name
 *   - a colour legend (what the shading means)
 *   - the live SOURCE/MODEL it's drawn from (e.g. "HRRR", "ICON Global"); for
 *     auto-selected OM layers this is a CHIP you click to pin a specific model
 *     (Auto by default), keeping the panel condensed.
 *
 * Service-driven: it reads/writes the layer service singletons directly and holds
 * no layer state of its own, so the same per-layer rows can later be mounted inside
 * the layer switcher without divergence.
 *
 * v1 scope: weather layers (NWM + OM, incl. the unified lightning layer).
 */

import { LitElement, html, css, nothing, type TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import type { Map as MlMap } from 'maplibre-gl'
import { OM_LAYERS, type OmLayerDef } from '../../services/terrain/om-types.js'
import {
  isOmLayerEnabled,
  getOmLayerActiveModelId,
  getOmLayerModelId,
  setOmLayerModel,
  getViewportModels,
} from '../../services/terrain/om-layer-service.js'
import {
  colormapForVariable,
  UNIFIED_LIGHTNING,
  type Colormap,
} from '../../services/terrain/om/om-colormap.js'
import { ALL_NWM_SERVICES, type NwmVariableService } from '../../services/terrain/nwm-service.js'
import { lightningUnifiedService } from '../../services/terrain/lightning-unified-service.js'
import { slopeAngleService } from '../../services/terrain/slope-angle-service.js'
import { LAYER_TIER_MAP } from '../../lib/layer-tier-registry.js'
import type { GradientLegendConfig } from '../../services/terrain/nwm-types.js'

// A normalized, host-agnostic description of one visible layer's "key" — built
// fresh on each render from the live services so source/model stay current.
interface KeyRow {
  id: string
  name: string
  /** One-line description shown when the (i) icon is tapped. */
  description: string
  /** Live source/model label (e.g. "HRRR", "ICON Global"); empty if none. */
  source: string
  /** CSS gradient for the legend bar, or null for classification/none. */
  gradient: string | null
  /** Min/max labels printed under the gradient (e.g. "0" … "75 dBZ"). */
  scaleTicks: string[] | null
  /** Discrete swatches for categorical layers (e.g. precip type). */
  swatches: { color: string; label: string }[] | null
  /** Model-pin options (Auto + models), or null if the layer isn't pinnable. */
  models: { id: string | null; label: string }[] | null
  selectedModelId: string | null // null = Auto
  setModel: (id: string | null) => void
}

// Minimal shape of a tile/WMS overlay the Key shows a legend for (subset of
// OverlayLayer; kept structural to avoid importing the switcher into this file).
interface LegendOverlay {
  id: string
  name: string
  description?: string
  gradientLegend?: GradientLegendConfig
  classificationLegend?: { color: string; label: string }[]
}

// Pull evenly-spaced tick labels from an NWM gradient legend's ticks (already
// labelled, e.g. "Low\n(0.04)"). Falls back to null when none.
function nwmScaleTicks(legend: GradientLegendConfig): string[] | null {
  if (!legend.ticks?.length) return null
  return legend.ticks.map(t => t.label.replace(/\n/g, ' '))
}

// Min/mid/max labels for an OM colormap, from its data domain + a unit suffix.
function scaleTicksFromColormap(cmap: Colormap, unit: string): string[] | null {
  if (!Number.isFinite(cmap.domainMin) || !Number.isFinite(cmap.domainMax)) return null
  const u = unit ? ` ${unit}` : ''
  const mid = (cmap.domainMin + cmap.domainMax) / 2
  const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1))
  return [`${fmt(cmap.domainMin)}`, `${fmt(mid)}`, `${fmt(cmap.domainMax)}${u}`]
}

// Per-OM-variable description + unit for the Key (registry carries no description).
const OM_VARIABLE_INFO: Record<string, { desc: string; unit: string }> = {
  cloud_cover: { desc: 'Total cloud cover.', unit: '%' },
  cloud_cover_high: { desc: 'High-altitude cloud cover.', unit: '%' },
  cloud_cover_mid: { desc: 'Mid-altitude cloud cover.', unit: '%' },
  cloud_cover_low: { desc: 'Low-altitude cloud cover.', unit: '%' },
  precipitation: { desc: 'Precipitation rate (rain-equivalent).', unit: 'mm/hr' },
  cape: { desc: 'Convective available potential energy — thunderstorm fuel.', unit: 'J/kg' },
  visibility: { desc: 'Surface horizontal visibility.', unit: 'm' },
  wind_speed_10m: {
    desc: 'Wind at 10 m — direction and sustained speed, with gusts.',
    unit: 'm/s',
  },
  temperature_2m: { desc: 'Air temperature at 2 m.', unit: '°C' },
  mass_density_8m: { desc: 'Near-surface smoke concentration.', unit: 'µg/m³' },
  freezing_level_height: { desc: 'Height of the 0 °C freezing level.', unit: 'm' },
  snowfall_height: { desc: 'Snow line elevation.', unit: 'm' },
  lightning_potential: { desc: 'Lightning potential index.', unit: 'J/kg' },
}

// Slope-angle ramp for the Key — a continuous gradient (not discrete bands) keyed to the
// SLOPE_STOPS colours in slope-worker.ts / the GPU shader. Stops are positioned at their
// true angle within the 27–50° range so the bar matches what the map paints. The rising
// gradient itself is the caution signal; the distinct anchor colours still read severity.
const SLOPE_GRADIENT =
  'linear-gradient(to right, ' +
  '#eab308 0%, ' + // 27° yellow
  '#f97316 13%, ' + // 30° orange
  '#ef4444 35%, ' + // 35° red
  '#9333ea 57%, ' + // 40° purple
  '#3b82f6 78%, ' + // 45° blue
  '#0f0f0f 100%)' // 50°+ black
const SLOPE_TICKS = ['27°', '30°', '35°', '40°', '45°', '50°+']

@customElement('layer-key-fab')
export class LayerKeyFab extends LitElement {
  // Bumped by the host (cairn-map) whenever layer state may have changed.
  @property({ type: Number }) revision = 0

  // The live map. We listen to its move/zoom so the source/model indicator stays
  // current even when the host isn't re-rendering (auto-select swaps on pan/zoom).
  @property({ attribute: false }) map: MlMap | null = null

  // Active WMS/tile overlays that carry a gradient legend (e.g. snow coverage),
  // supplied by cairn-map. Each contributes a Key row with its colour ramp.
  @property({ attribute: false }) overlays: LegendOverlay[] = []

  @state() private open = false
  @state() private tick = 0
  // Which row's model-pin dropdown is currently open (null = none).
  @state() private modelMenuFor: string | null = null
  // Which row's description is expanded (null = none).
  @state() private descFor: string | null = null

  private mapHandler = (): void => {
    this.tick++
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('map')) {
      const prev = changed.get('map') as MlMap | null
      if (prev) {
        prev.off('moveend', this.mapHandler)
        prev.off('zoomend', this.mapHandler)
      }
      if (this.map) {
        this.map.on('moveend', this.mapHandler)
        this.map.on('zoomend', this.mapHandler)
      }
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    if (this.map) {
      this.map.off('moveend', this.mapHandler)
      this.map.off('zoomend', this.mapHandler)
    }
  }

  static override styles = css`
    /* Upper-right of the map, BELOW the context bar. The collapsed context bar is
     * 60px tall and pinned to the top; sit clear of it. (Its --bar-height var is
     * scoped to that element, so we use the known collapsed height + a gap.) */
    :host {
      position: absolute;
      top: 72px;
      right: 12px;
      z-index: 1000;
      display: block;
      font-size: 13px;
      color: var(--cairn-text-color, #1a1a1a);
    }
    .fab {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-secondary-color, #64748b);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.15s ease;
    }
    .fab:active {
      transform: scale(0.95);
    }
    .fab.active {
      background: var(--cairn-primary-color, #1976d2);
      color: #ffffff;
    }
    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 22px;
    }
    .panel {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      width: 250px;
      max-height: 60vh;
      overflow-y: auto;
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, rgba(0, 0, 0, 0.1));
      border-radius: 10px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
      padding: 8px;
      box-sizing: border-box;
    }
    .panel-title {
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--cairn-text-secondary-color, #64748b);
      padding: 4px 6px 8px;
    }
    .row {
      padding: 8px 6px;
      border-top: 1px solid var(--cairn-border-color, #eee);
    }
    .row:first-of-type {
      border-top: none;
    }
    .row-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }
    .row-name {
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      gap: 2px;
    }
    /* (i) description toggle next to the layer name. */
    .info-btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      color: var(--cairn-text-muted-color, #94a3b8);
    }
    .info-btn:hover,
    .info-btn.active {
      color: var(--cairn-primary-color, #1976d2);
    }
    .info-btn .material-icons {
      font-size: 15px;
    }
    .row-desc {
      font-size: 11px;
      line-height: 1.4;
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 4px 0 2px;
    }
    /* Source/model indicator. Plain text in the normal body colour (a button when
     * pinnable, with a down-caret to signal it opens a chooser). No pill/outline. */
    .model-chip {
      font-size: 11px;
      font-weight: 600;
      color: var(--cairn-text-color, #1a1a1a);
      white-space: nowrap;
      background: none;
      border: none;
      padding: 0;
      cursor: default;
      display: inline-flex;
      align-items: center;
      gap: 1px;
    }
    button.model-chip {
      cursor: pointer;
    }
    button.model-chip:hover {
      color: var(--cairn-primary-color, #1976d2);
    }
    button.model-chip .material-icons {
      font-size: 16px;
      opacity: 0.7;
    }
    .model-menu {
      display: flex;
      flex-direction: column;
      margin: 4px 0 0;
      border: 1px solid var(--cairn-border-color, rgba(0, 0, 0, 0.1));
      border-radius: 6px;
      overflow: hidden;
    }
    .model-option {
      text-align: left;
      background: none;
      border: none;
      padding: 6px 8px;
      font-size: 12px;
      cursor: pointer;
      color: var(--cairn-text-color, #1a1a1a);
    }
    .model-option:hover {
      background: var(--cairn-surface-hover-color, rgba(0, 0, 0, 0.06));
    }
    .model-option.selected {
      font-weight: 700;
      color: var(--cairn-primary-color, #1976d2);
    }
    .legend-bar {
      height: 10px;
      border-radius: 3px;
      margin: 6px 0 2px;
      border: 1px solid var(--cairn-border-color, rgba(0, 0, 0, 0.1));
    }
    .scale-ticks {
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      color: var(--cairn-text-secondary-color, #64748b);
    }
    .swatches {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin: 6px 0 2px;
    }
    .swatch {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      color: var(--cairn-text-secondary-color, #555);
    }
    .swatch-box {
      width: 12px;
      height: 12px;
      border-radius: 2px;
      border: 1px solid var(--cairn-border-color, rgba(0, 0, 0, 0.15));
    }
    .empty {
      padding: 10px;
      color: var(--cairn-text-secondary-color, #888);
      font-size: 12px;
    }
  `

  private toggle(): void {
    this.open = !this.open
    if (!this.open) this.modelMenuFor = null
  }

  // ---- Build the live rows from the services (weather layers only, v1) ----

  private buildRows(): KeyRow[] {
    const rows: KeyRow[] = []

    if (lightningUnifiedService.enabled) {
      // Lightning is multi-source: pin options are Auto + whatever is available for
      // the current viewport (HRRR or LPI, plus the WMO fallback). Map source ids
      // onto the generic KeyRow model dropdown ('auto' → null).
      const opts = lightningUnifiedService.getSourceOptions()
      const sel = lightningUnifiedService.selectedSource
      rows.push({
        id: 'lightning-unified',
        name: LAYER_TIER_MAP.get('lightning-unified')?.name ?? 'Lightning',
        description:
          'Worldwide thunderstorm activity. Auto-picks the highest-fidelity source ' +
          'for the view: HRRR flash density (US), ICON-D2 lightning potential (C. Europe), ' +
          'or ICON Global storm category elsewhere.',
        source: lightningUnifiedService.activeSourceLabel,
        gradient: gradientFromColormap(UNIFIED_LIGHTNING),
        scaleTicks: ['None', 'Possible', 'Severe'],
        swatches: null,
        models: opts.map(o => ({ id: o.id === 'auto' ? null : o.id, label: o.label })),
        selectedModelId: sel === 'auto' ? null : sel,
        setModel: id =>
          lightningUnifiedService.setSource((id ?? 'auto') as 'auto' | 'hrrr' | 'lpi' | 'wx'),
      })
    }

    for (const def of OM_LAYERS) {
      if (!isOmLayerEnabled(def.id)) continue
      rows.push(this.omRow(def))
    }

    for (const svc of ALL_NWM_SERVICES) {
      if (!svc.enabled) continue
      rows.push(this.nwmRow(svc))
    }

    if (slopeAngleService.enabled) {
      rows.push({
        id: 'slope-angle',
        name: LAYER_TIER_MAP.get('slope-angle')?.name ?? 'Slope Angle Shading',
        description:
          'Steepness shading for avalanche terrain — colour ramps up with slope angle; ' +
          'slopes below 27° are not shaded.',
        source: '',
        gradient: SLOPE_GRADIENT,
        scaleTicks: SLOPE_TICKS,
        swatches: null,
        models: null,
        selectedModelId: null,
        setModel: () => {},
      })
    }

    // WMS/tile overlays with a colour ramp (e.g. snow coverage) or a discrete
    // classification key (e.g. vegetation disturbance), passed by the host.
    for (const ov of this.overlays) {
      if (!ov.gradientLegend && !ov.classificationLegend) continue
      rows.push({
        id: ov.id,
        // The registry name already qualifies grouped layers (e.g.
        // "Snow Coverage — SNPP"); prefer it over the bare config name ("SNPP")
        // so the row is meaningful out of its group context.
        name: LAYER_TIER_MAP.get(ov.id)?.name ?? ov.name,
        description: ov.description ?? '',
        source: '',
        gradient: ov.gradientLegend ? gradientFromNwmLegend(ov.gradientLegend) : null,
        scaleTicks: ov.gradientLegend ? nwmScaleTicks(ov.gradientLegend) : null,
        swatches: ov.classificationLegend ?? null,
        models: null,
        selectedModelId: null,
        setModel: () => {},
      })
    }

    return rows
  }

  private omRow(def: OmLayerDef): KeyRow {
    const activeId = getOmLayerActiveModelId(def.id)
    // Only models that can render this layer at the CURRENT viewport are offered.
    const models = getViewportModels(def)
    const active = models.find(m => m.id === activeId)
    const cmap = def.colormap ?? colormapForVariable(def.variable)
    return {
      id: def.id,
      name: LAYER_TIER_MAP.get(def.id)?.name ?? def.label,
      description: OM_VARIABLE_INFO[def.variable]?.desc ?? def.label,
      source: active ? formatModelLabel(active.label) : '',
      gradient: gradientFromColormap(cmap),
      scaleTicks: scaleTicksFromColormap(cmap, OM_VARIABLE_INFO[def.variable]?.unit ?? ''),
      swatches: null,
      models: [
        { id: null, label: 'Auto' },
        ...models.map(m => ({ id: m.id, label: formatModelLabel(m.label) })),
      ],
      selectedModelId: getOmLayerModelId(def.id),
      setModel: id => setOmLayerModel(def.id, id),
    }
  }

  private nwmRow(svc: NwmVariableService): KeyRow {
    const cfg = svc.variableConfig
    return {
      id: `nwm-${svc.id}`,
      name: LAYER_TIER_MAP.get(`nwm-${svc.id}`)?.name ?? cfg.label,
      description: cfg.description || cfg.label,
      source: nwmSourceLabel(cfg.source),
      gradient: cfg.legend ? gradientFromNwmLegend(cfg.legend) : null,
      scaleTicks: cfg.legend ? nwmScaleTicks(cfg.legend) : null,
      swatches: cfg.classificationLegend?.map(e => ({ color: e.color, label: e.label })) ?? null,
      models: null, // single-source (HRRR/NWM/GOES/MRMS); not pinnable
      selectedModelId: null,
      setModel: () => {},
    }
  }

  // ---- Render ----

  override render(): TemplateResult {
    const rows = this.buildRows()
    // The button is ALWAYS present; the panel shows an empty state when nothing's on.
    return html`
      <button class="fab ${this.open ? 'active' : ''}" title="Map key" @click=${this.toggle}>
        <span class="material-icons">info</span>
      </button>
      ${this.open
        ? html`
            <div class="panel">
              <div class="panel-title">Map Key</div>
              ${rows.length
                ? rows.map(r => this.renderRow(r))
                : html`<div class="empty">No layers visible. Turn on a layer to see its key.</div>`}
            </div>
          `
        : nothing}
    `
  }

  private renderRow(r: KeyRow): TemplateResult {
    const menuOpen = this.modelMenuFor === r.id
    const descOpen = this.descFor === r.id
    return html`
      <div class="row">
        <div class="row-head">
          <span class="row-name">
            ${r.name}
            ${r.description
              ? html`<button
                  class="info-btn ${descOpen ? 'active' : ''}"
                  title="About this layer"
                  @click=${() => {
                    this.descFor = descOpen ? null : r.id
                  }}
                >
                  <span class="material-icons">info</span>
                </button>`
              : nothing}
          </span>
          ${this.renderSource(r, menuOpen)}
        </div>

        ${descOpen && r.description ? html`<div class="row-desc">${r.description}</div>` : nothing}
        ${menuOpen && r.models ? this.renderModelMenu(r) : nothing}
        ${r.gradient
          ? html`<div class="legend-bar" style="background:${r.gradient}"></div>`
          : nothing}
        ${r.scaleTicks
          ? html`<div class="scale-ticks">${r.scaleTicks.map(t => html`<span>${t}</span>`)}</div>`
          : nothing}
        ${r.swatches
          ? html`<div class="swatches">
              ${r.swatches.map(
                s =>
                  html`<span class="swatch"
                    ><span class="swatch-box" style="background:${s.color}"></span>${s.label}</span
                  >`
              )}
            </div>`
          : nothing}
      </div>
    `
  }

  // The source/model indicator. Pinnable (OM) → a button that toggles the model
  // menu; otherwise plain text. Rendered identically (just the model label) so
  // every layer reads the same way.
  private renderSource(r: KeyRow, menuOpen: boolean): TemplateResult {
    if (!r.source && !r.models) return html`${nothing}`
    if (r.models) {
      return html`<button
        class="model-chip"
        title="Choose model"
        @click=${() => {
          this.modelMenuFor = menuOpen ? null : r.id
        }}
      >
        ${r.source || 'Auto'}<span class="material-icons">expand_more</span>
      </button>`
    }
    return html`<span class="model-chip">${r.source}</span>`
  }

  private renderModelMenu(r: KeyRow): TemplateResult {
    return html`
      <div class="model-menu">
        ${r.models?.map(
          m =>
            html`<button
              class="model-option ${(r.selectedModelId ?? '') === (m.id ?? '') ? 'selected' : ''}"
              @click=${() => {
                r.setModel(m.id)
                this.modelMenuFor = null
                this.requestUpdate()
              }}
            >
              ${m.label}${m.id === null && r.source ? ` · ${r.source}` : ''}
            </button>`
        )}
      </div>
    `
  }
}

// Unified model-label formatting across all rows: just the model's name (no
// resolution), so Lightning's "HRRR" and the OM layers' source read identically.
// (Resolution is dropped for brevity; the names like "ICON Global" / "ECMWF IFS"
// are distinct enough on their own.)
function formatModelLabel(label: string): string {
  return label
}

// Human-readable single-source label for an NWM layer's product.
function nwmSourceLabel(source: string): string {
  switch (source) {
    case 'hrrr':
      return 'HRRR'
    case 'goes':
      return 'GOES'
    case 'mrms':
      return 'MRMS'
    case 'land':
    case 'forcing':
      return 'NWM'
    default:
      return source.toUpperCase()
  }
}

// Build a CSS linear-gradient from an OM Colormap (stops carry t in 0..1, RGB,
// optional alpha). Mirrors how the GPU LUT ramps colour + alpha.
function gradientFromColormap(cmap: Colormap): string {
  const stops = cmap.stops.map(s => {
    const a = s.a == null ? 1 : s.a / 255
    const pct = Math.round(s.t * 100)
    return `rgba(${s.c[0]},${s.c[1]},${s.c[2]},${a}) ${pct}%`
  })
  return `linear-gradient(to right, ${stops.join(', ')})`
}

// Build a CSS linear-gradient from an NWM gradient legend config.
function gradientFromNwmLegend(legend: GradientLegendConfig): string {
  const stops = legend.stops.map(s => `${s.color} ${Math.round(s.position * 100)}%`)
  return `linear-gradient(to right, ${stops.join(', ')})`
}

declare global {
  interface HTMLElementTagNameMap {
    'layer-key-fab': LayerKeyFab
  }
}
